import { createSlice } from "@reduxjs/toolkit";
import { IUserData, IMessagesData, IUserByIdData, ICreatedGroup, IUserStore, IAttachment } from 'src/models/messenger';
import axios from "src/utils/axios";
import { FailureSnackbar} from "./snackbar";
import { sendChatMessage } from "./livekitMeeting";
// import { leaveRoom, sendChatMessage } from "./livekitMeeting";
// import { playSound } from "src/utils/soundManager";

interface IMessenger {
  user_id: string;
  name: string;
  userData: IUserData[],
  pinnedData: IUserData[],
  hiddenData: IUserData[],
  messageByIdData: IMessagesData[],
  userById: IUserByIdData,
  createdGroup: ICreatedGroup[],
  message: { id: number, edit: boolean, value: string },
  openCodeEditor: boolean,
  attachmentLength: number,
  send_threadId: string,
  userStore: IUserStore[],
  threadId: number,
  size: {
    width: number, height: number
  },
  zoomSize: {
    width: number, height: number
  },
  inputRow: number,
  chatToggle: boolean | string,
  unread: boolean,
  messageData: string,
  errMsg:string,
  loader:boolean,
  attachment: IAttachment[],
  left:boolean,
  poorConnectionWarn: boolean,
  connectionWarnAlreadyShown: boolean,
  replyingTo: { id: number, content: string, createdBy: string } | null,
  dmThreadId: string | null,
  dmMessages: IMessagesData[],
  dmPeer: { id: string; name: string } | null,
  dmUnread: Record<string, boolean>,
  dmUnreadCounts: Record<string, number>,
  dmPreviews: Record<string, { content?: string, contentType?: string, createdBy?: string, isReaction?: boolean, emojiCode?: string, userId?: string }>,
  dmClosedBy: string[],
  dmLoading: boolean,
}

const initialState: IMessenger = {
  user_id: null,
  name: "",
  userData: [],
  pinnedData: [],
  hiddenData: [],
  messageByIdData: [],
  userById: null,
  attachmentLength: 0,
  createdGroup: [],
  openCodeEditor: false,
  message: {
    id: null,
    edit: false,
    value: null
  },
  send_threadId: null,
  userStore: [],
  threadId: null,
  size: {
    width: 300, height: 400
  },
  zoomSize: {
    width: 300, height: 400
  },
  inputRow: 1,
  chatToggle: false as boolean | string,
  unread: false,
  messageData: '',
  errMsg:'',
  loader:false,
  attachment: [],
  left:false,
  poorConnectionWarn: false,
  connectionWarnAlreadyShown: false,
  replyingTo: null,
  dmThreadId: null,
  dmMessages: [],
  dmPeer: null,
  dmUnread: {},
  dmUnreadCounts: {},
  dmPreviews: {},
  dmClosedBy: [],
  dmLoading: false,
};

const slice = createSlice({
  name: "messenger",
  initialState,
  reducers: {
    userData(state, action) {
      state.userData = action.payload;
    },
    pinnedData(state, action) {
      state.pinnedData = action.payload;
    },
    hiddenData(state, action) {
      state.hiddenData = action.payload;
    },
    messageByIdData(state, action) {
      state.messageByIdData = action.payload;
    },
    userById(state, action) {
      state.userById = action.payload;
    },
    createdGroup(state, action) {
      state.createdGroup = action.payload;
    },
    editMessage(state, action) {
      state.message = action.payload;
    },
    setUserId(state, action) {
      state.user_id = action.payload.sub;
      state.name = action.payload.name;
    },
    setSendThreadId(state, action) {
      state.send_threadId = action.payload;
    },
    storeAllUser(state, action) {
      state.userStore = action.payload;
    },
    storeThreadId(state, action) {
      state.threadId = action.payload;
    },
    codeEdit(state, action) {
      state.openCodeEditor = action.payload;
    },
    attachmentLengthStore(state, action) {
      state.attachmentLength = action.payload;
    },
    sizeController(state, action) {
      state.size = action.payload;
    },
    zoomSizeController(state, action) {
      state.zoomSize = action.payload;
    },
    storeInputRow(state, action) {
      state.inputRow = action.payload;
    },
    changeChatToggleValue(state, action) {
      state.chatToggle = action.payload;
      if (state.chatToggle) {
        state.unread = false;
      }
      state.replyingTo = null;
      if (action.payload !== 'dm') {
        state.dmThreadId = null;
        state.dmMessages = [];
        state.dmPeer = null;
        // Do not clear dmClosedBy, so the status isn't lost if they return, but clearing peer/thread is enough
      }
    },
    unreadToggle(state) {
      if (!state.chatToggle) {
        state.unread = true;
      }
      else {
        state.unread = false;
      }
    },
    storeMessage(state, action) {
      state.messageData = action.payload;
    },
    setLoading(state, action) {
      state.loader = action.payload;
    },

    storeErrorMessage(state,action){
      state.errMsg=action.payload;
    },

    storeAttachments(state, action) {
      state.attachment = [...state.attachment, action.payload];
    },
    clearStoreAttachments(state, action) {
      state.attachment = action.payload;
    },
    left(state,action){
      state.left=action.payload;
    },
    setPoorConnectionWarn(state, action) {
      state.poorConnectionWarn = action.payload;
    },
    setConnectionWarnAlreadyShown(state, action) {
      state.connectionWarnAlreadyShown = action.payload;
    },
    setReplyingTo(state, action) {
      state.replyingTo = action.payload;
    },
    clearReplyingTo(state) {
      state.replyingTo = null;
    },
    setDmThread(state, action) {
      state.dmThreadId = action.payload.threadId;
      state.dmClosedBy = action.payload.closedBy || [];
    },
    setDmMessages(state, action) {
      state.dmMessages = action.payload;
    },
    setDmPeer(state, action) {
      state.dmPeer = action.payload;
      state.replyingTo = null;
    },
    clearDm(state) {
      state.dmThreadId = null;
      state.dmMessages = [];
      state.dmPeer = null;
      state.dmClosedBy = [];
      state.replyingTo = null;
      // keep dmUnread + dmUnreadCounts so the badge persists after going back
    },
    markDmUnread(state, action) {
      state.dmUnread = { ...state.dmUnread, [action.payload]: true };
    },
    setDmUnreadCount(state, action) {
      const { peerId, count, lastMessage } = action.payload as { peerId: string; count: number; lastMessage?: any };
      state.dmUnreadCounts = { ...state.dmUnreadCounts, [peerId]: count };
      if (lastMessage) {
        state.dmPreviews = { ...state.dmPreviews, [peerId]: lastMessage };
      }
      if (count > 0) {
        state.dmUnread = { ...state.dmUnread, [peerId]: true };
      } else {
        const next = { ...state.dmUnread };
        delete next[peerId];
        state.dmUnread = next;
      }
    },
    clearDmUnread(state, action) {
      const next = { ...state.dmUnread };
      next[action.payload] = false; // instead of deleting, set to false
      state.dmUnread = next;
      const nextCounts = { ...state.dmUnreadCounts };
      nextCounts[action.payload] = 0; // instead of deleting, set to 0 to keep peer in dmAllPeers
      state.dmUnreadCounts = nextCounts;
    },
    addDmClosedBy(state, action) {
      if (!state.dmClosedBy.includes(action.payload)) {
        state.dmClosedBy = [...state.dmClosedBy, action.payload];
      }
    },
    removeDmClosedBy(state, action) {
      state.dmClosedBy = state.dmClosedBy.filter(id => id !== action.payload);
    },
    setDmLoading(state, action) {
      state.dmLoading = action.payload;
    },
  }
});

export const reducer = slice.reducer;
export const actions=slice.actions;
const BASE_URL = process.env.REACT_APP_API_URL;

const link = `${BASE_URL}/communication/api/v1/messaging/threads`;

export const messageApiById = (index) => async (dispatch) => {
  try {
    if (index) {
      const response = await axios.get(`${link}/${index}/message`,
        {
          headers: {
            'Cache-Control': 'no-cache  ',
            'Pragma': 'no-cache',
            'Expires': '0',
          }
        });
      dispatch(slice.actions.messageByIdData(response?.data?.data));

      dispatch(slice.actions.unreadToggle());
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const sendMessageById = (data) => async (dispatch) => {
  try {
    if (data?.attachment?.length > 0 && data?.msg !== '') {
      const message = {
        eventType: "event_message",
        contentType: data?.contentType,
        content: data?.msg,
        attachment: data?.attachment,
        userId: data?.userId,
        createdBy: data?.userName,
        replyTo: data?.replyTo
      };
      const response = await axios.post(`${link}/${data?.id}/message`, message);
      if (response?.status === 200) {
        dispatch(messageApiById(data?.id));
      }
    }
    else if (data?.attachment?.length > 0) {
      const message = {
        eventType: "event_message", 
        contentType: data?.contentType,
        attachment: data?.attachment,
        userId: data?.userId,
        createdBy: data?.userName,
        replyTo: data?.replyTo
      };
      const response = await axios.post(`${link}/${data?.id}/message`, message);
      if (response?.status === 200) {
        dispatch(messageApiById(data?.id));
      }
    }
    else {
      const message = {
        eventType: "event_message",
        contentType: data?.contentType,
        content: data?.msg,
        userId: data?.userId,
        createdBy: data?.userName,
        replyTo: data?.replyTo
      };
      const response = await axios.post(`${link}/${data?.id}/message`, message);
      if (response?.status === 200) {
        dispatch(messageApiById(data?.id));
        if (!data?.attachment?.length) {
        dispatch(sendChatMessage(message));
      }
      }
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const denyHost = () => async (dispatch) => {
  dispatch(slice.actions.left(true));
};

// export const leftMessages = (data) => async (dispatch,getState) => {
//   try {
//     dispatch(slice.actions.left(true));
//     const message = {
//       eventType: "left_message",
//       content: `${data?.userName} left the meeting.`,
//     };
//     if(getState().meeting.soundNotificationonJoinOrLeave){
//       playSound("/sounds/sound-effect-4336.mp3");
//     }
//     const response = await axios.post(`${link}/${data?.id}/message`, message);
//     if (response?.status === 200) {
//       dispatch(messageApiById(data?.id));
//     }
//     dispatch(leaveRoom());
//   } catch (error) {
//     throw new Error(error);
//   }
// };

export const handleRemoveAllUser = () => async (dispatch:any) => {
  try {
    const response = await axios.post(`${BASE_URL}/events/api/v1/events/endcall`);
    if (!response.data.status) {
      dispatch(FailureSnackbar({
        message: "Failed to remove all users.",
        open: true,
      }));
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const emojiMessage = (data) => async (dispatch) => {
  try {
    await axios.post(`${link}/${data?.id}/message/${data?.messageId}/${data?.emoji?.userId}`, data?.emoji);
    if (data?.chatType === 'dm') {
      dispatch(fetchDMMessages(data?.id));
    } else {
      dispatch(messageApiById(data?.id));
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const deleteEmoji = (data) => async (dispatch) => {
  try {
    await axios.put(`${link}/${data?.id}/message/${data?.messageId}/${data?.userId}`);
    if (data?.chatType === 'dm') {
      dispatch(fetchDMMessages(data?.id));
    } else {
      dispatch(messageApiById(data?.id));
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const deleteMessageById = (data) => async (dispatch) => {
  try {
    dispatch(slice.actions.setLoading(true));
    const response = await axios.delete(`${link}/${data?.id}/message/${data?.messageId}/${data?.userId}`);
    if (response?.status === 200) {
      await dispatch(messageApiById(data?.id));
    }
  } catch (error) {
    throw new Error(error);
  } finally {
    dispatch(slice.actions.setLoading(false));
  }
};

export const editTrue = (data) => async (dispatch) => {
  dispatch(slice.actions.editMessage(data));
};

export const editFalse = (data) => async (dispatch) => {
  try {
    const message = {
      content: data?.value?.msg,
      attachment: data?.value?.attachment,
      userId: data?.value?.userId,
    };
    await axios.put(`${link}/${data?.userId}/message/${data?.id}`, message);
    dispatch(slice.actions.editMessage(data));

    dispatch(messageApiById(data?.userId));
  } catch (error) {
    throw new Error(error);
  }
};

export const addUserId = (data) => async (dispatch) => {
  dispatch(slice.actions.setUserId(data));
};

export const storeSendThreadId = (data) => async (dispatch) => {
  dispatch(slice.actions.setSendThreadId(data));
};

export const openCodeEditorController = (data) => async (dispatch) => {
  dispatch(slice.actions.codeEdit(data));
};

export const attachmentLengthController = (data) => async (dispatch) => {
  dispatch(slice.actions.attachmentLengthStore(data));
};

export const storeSize = (data) => (dispatch) => {
  dispatch(slice.actions.sizeController(data));
};

export const storeZoomSize = (data) => (dispatch) => {
  dispatch(slice.actions.zoomSizeController(data));
};

export const inputRowController = (data) => (dispatch) => {
  dispatch(slice.actions.storeInputRow(data));
};

export const chatToggleHandler = (data) => (dispatch) => {
  dispatch(slice.actions.changeChatToggleValue(data));
};

export const saveMessageData = (data) => (dispatch) => {
  dispatch(slice.actions.storeMessage(data));
};

export const saveAttachments = (data) => (dispatch) => {
  dispatch(slice.actions.clearStoreAttachments(data));
};

export const setPoorConnectionWarn = (data) => (dispatch) => {
  dispatch(slice.actions.setPoorConnectionWarn(data));
};

export const setConnectionWarnAlreadyShown = (data) => (dispatch) => {
  dispatch(slice.actions.setConnectionWarnAlreadyShown(data));
};

export const setReplyingToData = (data) => (dispatch) => {
  dispatch(slice.actions.setReplyingTo(data));
};

export const clearReplyingToData = () => (dispatch) => {
  dispatch(slice.actions.clearReplyingTo());
};

export const fileuploading = (data) => async (dispatch) => {
  try {
  dispatch(slice.actions.setLoading(true));

  const formData = new FormData();
  formData.append('image', data);

    const uploadImageResponse = await axios.post(`${BASE_URL}/communication/api/v1/events/image`, formData);

  dispatch(slice.actions.setLoading(false));

    if (uploadImageResponse?.data?.image) {
    const fileData = {
      id: Date.now(),
      fileName: data?.name,
      fileSize: Number((data?.size / 1024 / 1024).toFixed(2)),
      unit: 'MB',
      link: uploadImageResponse?.data?.image,
      fileType: data?.name?.split('.')[1],
    };
      dispatch(slice.actions.storeAttachments(fileData));
  }
  } catch (error) {
    dispatch(slice.actions.storeErrorMessage(error?.message));
    throw new Error(error);
  } finally {
    dispatch(slice.actions.setLoading(false));
  }
};

export const fileDelete = (data) => async (dispatch) => {
  try {
    await axios.delete(`${BASE_URL}/communication/api/v1/events/image/delete`, {
      data: {
        url: data
      }
    });
  } catch (error) {
    dispatch(slice.actions.storeErrorMessage(error?.message));
    throw new Error(error);
  }
};

export const getOrCreateDMThread = (userAId: string, userBId: string, contextId: string, peerName: string) => async (dispatch) => {
  try {
    dispatch(slice.actions.setDmLoading(true));
    const response = await axios.post(`${link}/dm`, { userAId, userBId, contextId });
    const { threadId, closedBy } = response?.data?.data || {};
    dispatch(slice.actions.setDmThread({ threadId, closedBy: closedBy || [] }));
    dispatch(slice.actions.setDmPeer({ id: userBId, name: peerName }));
    if (threadId) {
      const msgs = await axios.get(`${link}/${threadId}/message`);
      dispatch(slice.actions.setDmMessages(msgs?.data?.data || []));
    }
  } catch (error: any) {
    const errorMsg = error?.response?.data?.message || error?.response?.data?.data || error?.message || (typeof error === 'string' ? error : 'Failed to start DM');
    dispatch(
      FailureSnackbar({
        message: errorMsg,
        open: true,
      })
    );
    throw new Error(errorMsg);
  } finally {
    dispatch(slice.actions.setDmLoading(false));
  }
};

export const fetchDMMessages = (dmThreadId: string) => async (dispatch) => {
  try {
    const response = await axios.get(`${link}/${dmThreadId}/message`);
    dispatch(slice.actions.setDmMessages(response?.data?.data || []));
  } catch (error) {
    throw new Error(error);
  }
};

export const sendDMMessage = (data) => async (dispatch) => {
  try {
    const message = {
      eventType: 'event_message',
      contentType: data?.contentType,
      content: data?.msg,
      attachment: data?.attachment,
      userId: data?.userId,
      createdBy: data?.userName,
      replyTo: data?.replyTo,
      senderParticipantId: data?.senderParticipantId,
      contextId: data?.contextId,
    };
    const response = await axios.post(`${link}/dm/${data?.dmThreadId}/message`, message);
    if (response?.data?.status === false || response?.data?.statusCode === 403) {
      dispatch(
        FailureSnackbar({
          message: response?.data?.message || response?.data?.data || 'Failed to send DM',
          open: true,
        })
      );
    } else if (response?.data?.statusCode === 200 || (response?.status === 200 && !response?.data?.statusCode)) {
      dispatch(fetchDMMessages(data?.dmThreadId));
    } else {
      dispatch(
        FailureSnackbar({
          message: response?.data?.message || response?.data?.data || 'Failed to send DM',
          open: true,
        })
      );
    }
  } catch (error: any) {
    const errorMsg = error?.message || error?.data || error?.response?.data?.message || error?.response?.data?.data || (typeof error === 'string' ? error : 'Failed to send DM');
    dispatch(
      FailureSnackbar({
        message: errorMsg,
        open: true,
      })
    );
    throw new Error(errorMsg);
  }
};

export const closeDMThread = (dmThreadId: string, contextId: string, requesterParticipantId: string) => async (dispatch) => {
  try {
    const response = await axios.put(`${link}/dm/${dmThreadId}/close`, { contextId, requesterParticipantId });
    if (response?.data?.data?.closedBy) {
      dispatch(slice.actions.addDmClosedBy(requesterParticipantId));
    }
  } catch (error) {
    throw new Error(error);
  }
};

export const reopenDMThread = (dmThreadId: string, contextId: string, requesterParticipantId: string) => async (dispatch) => {
  try {
    const response = await axios.put(`${link}/dm/${dmThreadId}/reopen`, { contextId, requesterParticipantId });
    if (response?.data?.data?.success) {
      dispatch(slice.actions.removeDmClosedBy(requesterParticipantId));
    }
  } catch (error) {
    throw new Error(error);
  }
};

/**
 * Called on join/refresh — restores dmUnread state from persisted DM threads.
 * Uses the real unreadCount from the backend (readBy-based) to show accurate badges.
 */
export const fetchMyDMThreads = (userId: string, contextId: string) => async (dispatch) => {
  try {
    const response = await axios.get(`${link}/dm`, {
      params: { contextId, userId },
    });
    const threads: Array<{ threadId: string; peerId: string; unreadCount: number; closedBy: string[]; lastMessage?: any }> =
      response?.data?.data || [];
    threads.forEach((thread) => {
      if (thread.peerId) {
        dispatch(slice.actions.setDmUnreadCount({ 
          peerId: thread.peerId, 
          count: thread.unreadCount || 0,
          lastMessage: thread.lastMessage
        }));
      }
    });
  } catch {
    // non-critical — silently swallow
  }
};

/**
 * Marks all messages in a DM thread as read for the current user.
 * Called when a user opens a DM conversation.
 */
export const markDMAsRead = (dmThreadId: string, peerId: string, userId: string) => async (dispatch) => {
  try {
    await axios.put(`${link}/dm/${dmThreadId}/read`, { userId });
    dispatch(slice.actions.clearDmUnread(peerId));
  } catch {
    // non-critical
  }
};

export const clearDmState = () => (dispatch) => {
  dispatch(slice.actions.clearDm());
};
